import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RefreshCw } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import {
  getNightlyRun,
  getNightlyRuns,
  getNightlySchedule,
  type NightlyRun,
  type NightlyRunDetailResponse,
  type NightlyRunSample,
  type NightlyRunsResponse,
  type NightlyScheduleNight,
  type NightlyScheduleResponse,
} from '../services/adminApi';
import {
  appServiceSkuCostPerHour,
  observedRunCost,
  APP_SERVICE_SKU_RATES,
  SQL_IDLE_VCORES,
} from '../services/azurePricing';
import {
  Banner,
  ButtonRow,
  Card,
  CardDescription,
  CardTitle,
  Metric,
  PrimaryButton,
  SecondaryButton,
  StatGrid,
} from '../components/admin/AdminUI';
import { borderRadius, colors, shadows, spacing, typography } from '../styles/theme';
import { displayEntityTaxonomyText, displayEntityType } from '../utils/entityTerminology';

const EMPTY_SAMPLES: NightlyRunSample[] = [];

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  align-items: flex-end;
  margin-bottom: ${spacing[4]};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Select = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${colors.white};
  font-size: ${typography.fontSizes.sm};
`;

const Th = styled.th<{ align?: string }>`
  padding: ${spacing[2]};
  text-align: ${p => p.align || 'left'};
  background: ${colors.gray[50]};
  color: ${colors.text.secondary};
  border-bottom: 1px solid ${colors.border.light};
  font-size: 11px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td<{ align?: string; muted?: boolean }>`
  padding: ${spacing[2]};
  text-align: ${p => p.align || 'left'};
  color: ${p => p.muted ? colors.text.tertiary : colors.text.primary};
  border-bottom: 1px solid ${colors.border.light};
  vertical-align: top;
  white-space: nowrap;
`;

const RunRow = styled.tr<{ selected?: boolean }>`
  cursor: pointer;
  background: ${p => p.selected ? colors.primary[50] : colors.white};

  &:hover {
    background: ${p => p.selected ? colors.primary[100] : colors.gray[50]};
  }
`;

const StatusBadge = styled.span<{ tone: 'success' | 'error' | 'warn' | 'info' }>`
  display: inline-block;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.md};
  font-size: 11px;
  font-weight: ${typography.fontWeights.medium};
  background: ${p =>
    p.tone === 'success' ? colors.success[50] :
    p.tone === 'error' ? colors.error[50] :
    p.tone === 'warn' ? colors.warning[50] :
    colors.gray[100]};
  color: ${p =>
    p.tone === 'success' ? colors.success[700] :
    p.tone === 'error' ? colors.error[700] :
    p.tone === 'warn' ? colors.warning[700] :
    colors.text.secondary};
`;

const BarTrack = styled.div`
  width: 110px;
  height: 8px;
  background: ${colors.gray[100]};
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div<{ width: number }>`
  width: ${p => Math.max(0, Math.min(100, p.width))}%;
  height: 100%;
  background: ${colors.primary[500]};
`;

const MiniBar = styled.div<{ width: number; tone?: 'primary' | 'success' | 'warn' }>`
  height: 6px;
  width: ${p => Math.max(3, Math.min(100, p.width))}%;
  border-radius: 999px;
  background: ${p =>
    p.tone === 'success' ? colors.success[500] :
    p.tone === 'warn' ? colors.warning[500] :
    colors.primary[500]};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 150px;
`;

/**
 * The queue name sizes to its content and the count takes the rest. A fixed
 * first column truncated nothing but let long names such as
 * website-discovery-dispatch overflow their cell and sit on top of the count.
 */
const QueueLine = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  gap: ${spacing[4]};
  align-items: baseline;
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
`;

const QueueCount = styled.code`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${colors.text.primary};
`;

const QueueTotal = styled.div`
  display: flex;
  gap: ${spacing[2]};
  align-items: center;
  margin-bottom: 2px;
`;

const PhaseBadge = styled.span`
  display: inline-block;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.sm};
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  white-space: nowrap;
`;

const SampleSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[4]};
`;

const SampleSummaryLabel = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SampleSummaryValue = styled.div`
  font-size: ${typography.fontSizes.xl};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  font-variant-numeric: tabular-nums;
`;

const SampleSummaryHint = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.tertiary};
  line-height: 1.4;
`;

const Pre = styled.pre`
  max-height: 220px;
  overflow: auto;
  padding: ${spacing[3]};
  background: ${colors.gray[50]};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.border.light};
  font-size: ${typography.fontSizes.xs};
`;

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function duration(seconds?: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function dash(value: unknown): string {
  return value == null || value === '' ? '—' : String(value);
}

function statusTone(status?: string): 'success' | 'error' | 'warn' | 'info' {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'deadline-exceeded') return 'error';
  if (status === 'running') return 'warn';
  return 'info';
}

function workloadLabel(run: NightlyRun): string {
  if (run.workload === 'webiq') return 'WebIQ refresh';
  return displayEntityTaxonomyText(run.entityLabel)
    || run.entityTypes?.map(displayEntityType).join(', ')
    || 'Extract';
}

function queueDepthEntries(sample: NightlyRunSample): Array<[string, number | null]> {
  const entries = Object.entries(sample.queueDepths || {})
    .filter(([, value]) => value == null || typeof value === 'number')
    .map(([name, value]) => [name, value] as [string, number | null]);
  if (entries.length > 0) return entries;
  return [['partner-processing', sample.queueDepth ?? null]];
}

function totalQueueDepth(sample: NightlyRunSample): number {
  return queueDepthEntries(sample).reduce((sum, [, depth]) => sum + (depth || 0), 0);
}

function maxQueue(samples: NightlyRunSample[]): number {
  return Math.max(1, ...samples.map(totalQueueDepth));
}

function maxCount(samples: NightlyRunSample[], key: 'functionInstanceCount' | 'readerReplicas'): number {
  return Math.max(1, ...samples.map(s => s[key] || 0));
}

/**
 * Human labels for the phases `scripts/nightly-run.sh` tags samples with.
 * Unknown values fall through to the raw string so a newly added phase shows up
 * rather than silently rendering blank.
 */
const PHASE_LABELS: Record<string, string> = {
  startup: 'Startup',
  preflight: 'Extraction preflight',
  'scale-up': 'Scale up',
  'source-cleanup': 'Source cleanup',
  'webiq-refresh': 'WebIQ refresh',
  'extraction-drain': 'Extraction drain',
  'post-processing': 'Post-processing',
  'scale-down': 'Scale down',
};

/** How much of the run's wall clock the samples actually observe. */
function sampleCoverage(samples: NightlyRunSample[], runSeconds?: number | null) {
  if (!runSeconds || runSeconds <= 0 || samples.length === 0) return { pct: null as number | null };
  const elapsed = samples
    .map(s => s.elapsedSeconds)
    .filter((v): v is number => typeof v === 'number');
  if (elapsed.length === 0) return { pct: null as number | null };
  // Samples describe from the first observation to the end of the run: the last
  // sample's configuration holds until the run stops.
  const first = Math.min(...elapsed);
  const covered = Math.max(0, runSeconds - first);
  return { pct: Math.min(100, Math.round((covered / runSeconds) * 100)) };
}

function percent(value?: number | null): string {
  return value == null ? '—' : `${Number(value).toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

function metricLine(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(' / ') || '—';
}

// Presentation order and labels for the counters the runner records for a
// WebIQ refresh, so the page explains what the run produced instead of dumping
// raw JSON.
const WEBIQ_STAT_LABELS: Array<[string, string]> = [
  ['entitiesQueued', 'Entities searched'],
  ['totalRuns', 'Discovery runs'],
  ['completedRuns', 'Completed'],
  ['sourcesActivatedRuns', 'Runs that found sources'],
  ['noResultRuns', 'Runs with no usable source'],
  ['failedRuns', 'Failed'],
  ['dailyQuotaDeferredRuns', 'Daily-quota deferred'],
  ['recoveryAttempted', 'Stale-run recovery attempted'],
  ['recoveryClaimed', 'Stale runs claimed'],
  ['recoveryEnqueued', 'Stale runs requeued'],
  ['recoveryErrors', 'Stale-run recovery errors'],
  ['acceptedSources', 'Sources activated'],
  ['newEventSources', 'New event sources added'],
  ['status', 'Batch status'],
  ['drainReason', 'Drain outcome'],
  ['processId', 'Process ID'],
];

function webIqStatValue(stats: Record<string, any>, key: string): string {
  const raw = stats[key];
  if (raw == null || raw === '' || raw === 'n/a') return '—';
  const numeric = Number(raw);
  return Number.isFinite(numeric) && /^-?[0-9]+(\.[0-9]+)?$/.test(String(raw))
    ? numeric.toLocaleString()
    : String(raw);
}

function scheduleWorkloadLabel(night: NightlyScheduleNight): string {
  return night.workload === 'webiq'
    ? 'WebIQ refresh'
    : `Event extraction — ${displayEntityTaxonomyText(night.entityLabel)}`;
}

export default function AdminNightlyRunsPage() {
  const [status, setStatus] = useState('');
  const [workload, setWorkload] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState<NightlyRunsResponse | null>(null);
  const [detail, setDetail] = useState<NightlyRunDetailResponse | null>(null);
  const [schedule, setSchedule] = useState<NightlyScheduleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const PAGE_SIZE = 10;

  const loadList = useCallback(async (pg = page) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getNightlyRuns({
        page: pg,
        pageSize: PAGE_SIZE,
        status: status || undefined,
        workload: workload || undefined,
      });
      setList(data);
      setPage(pg);
      if (!detail && data.runs.length > 0) {
        setDetail(await getNightlyRun(data.runs[0].runId));
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load nightly runs');
    } finally {
      setLoading(false);
    }
  }, [detail, page, status, workload]);

  const selectRun = async (runId: string) => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await getNightlyRun(runId));
    } catch (e: any) {
      setError(e?.message || 'Failed to load nightly run');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList(1);
    // The schedule is independent of the filters, so a failure here must not
    // take the run history down with it.
    getNightlySchedule({ nights: 9, history: 2 })
      .then(setSchedule)
      .catch(() => setSchedule(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runs = list?.runs ?? [];
  const samples = detail?.samples ?? EMPTY_SAMPLES;
  const queueMax = maxQueue(samples);
  const functionMax = maxCount(samples, 'functionInstanceCount');
  const readerMax = maxCount(samples, 'readerReplicas');
  const selectedRun = detail?.run;

  const coverage = useMemo(
    () => sampleCoverage(samples, selectedRun?.durationSeconds),
    [samples, selectedRun?.durationSeconds],
  );

  /**
   * What the run actually cost, integrated from its own samples rather than
   * from the planned configuration, so a run that scaled differently than
   * intended reports what really happened.
   */
  const runCost = useMemo(() => {
    if (samples.length === 0) return null;
    return observedRunCost(samples, {
      runSeconds: selectedRun?.durationSeconds ?? null,
      skuCostPerHour: appServiceSkuCostPerHour,
      baselineFunctionSkuCostPerHour: APP_SERVICE_SKU_RATES.P0v3.costPerHour,
      baselineFunctionInstances: 1,
      baselineSqlVCores: SQL_IDLE_VCORES,
    });
  }, [samples, selectedRun?.durationSeconds]);

  return (
    <AdminLayout
      currentPage="nightlyRuns"
      pageTitle="Nightly Run History"
      pageDescription="Container Apps job history, split WebIQ schedule, queue drain samples, scale state, and results."
    >
      {selectedRun && (
        <StatGrid style={{ marginBottom: spacing[6] }}>
          <Metric label="Status" value={selectedRun.status} tone={statusTone(selectedRun.status) === 'error' ? 'error' : 'neutral'} />
          <Metric label="Workload" value={workloadLabel(selectedRun)} />
          <Metric label="Duration" value={duration(selectedRun.durationSeconds)} />
          <Metric label="Enqueued" value={selectedRun.enqueuedCount?.toLocaleString() || '—'} />
          <Metric label="Function scale-up" value={`${selectedRun.scaleUpSku || '—'} x${selectedRun.scaleUpInstances ?? '—'}`} />
          <Metric label="SQL vCores" value={`${selectedRun.sqlUpVCores ?? '—'} → ${selectedRun.sqlScaleDownVerified ? '2 verified' : '—'}`} />
        </StatGrid>
      )}

      <Card style={{ marginBottom: spacing[4] }}>
        <CardTitle>Filters</CardTitle>
        <FiltersRow>
          <Field>
            Status
            <Select aria-label="Status" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="running">Running</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
              <option value="deadline-exceeded">Deadline exceeded</option>
            </Select>
          </Field>
          <Field>
            Workload
            <Select aria-label="Workload" value={workload} onChange={e => setWorkload(e.target.value)}>
              <option value="">All workloads</option>
              <option value="extract">Extract</option>
              <option value="webiq">WebIQ</option>
            </Select>
          </Field>
          <PrimaryButton onClick={() => loadList(1)} disabled={loading}>
            {loading ? <RefreshCw size={16} className="spin" /> : <RefreshCw size={16} />}
            {loading ? 'Loading…' : 'Load Runs'}
          </PrimaryButton>
        </FiltersRow>
      </Card>

      {error && <Banner tone="error" style={{ marginBottom: spacing[4] }}>{error}</Banner>}

      <Card style={{ marginBottom: spacing[4] }}>
        <CardTitle>Upcoming nights</CardTitle>
        {schedule?.schedules && (
          <CardDescription>
            {schedule.schedules.map(item => `${item.runnerName}: ${item.cronSchedule}`).join(' · ')}
          </CardDescription>
        )}
        {!schedule && <Banner tone="info">Schedule unavailable.</Banner>}
        {schedule && (
          <TableWrapper>
            <Table aria-label="Upcoming nights">
              <thead>
                <tr>
                  <Th>Runs at</Th>
                  <Th>Night</Th>
                  <Th>Runner</Th>
                  <Th>Workload</Th>
                  <Th>Last run</Th>
                  <Th>Previous run</Th>
                </tr>
              </thead>
              <tbody>
                {schedule.nights.map(night => {
                  const [last, previous] = night.previousRuns;
                  return (
                    <tr key={night.triggerAt}>
                      <Td>{fmtDate(night.triggerAt)}</Td>
                      <Td>{night.nightName}</Td>
                      <Td><code>{night.runnerName || 'job-pef-nightly'}</code></Td>
                      <Td title={displayEntityTaxonomyText(night.runLabel)}>{scheduleWorkloadLabel(night)}</Td>
                      <Td><PreviousRun run={last} /></Td>
                      <Td><PreviousRun run={previous} /></Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <Card style={{ marginBottom: spacing[4] }}>
        <CardTitle>Run history</CardTitle>
        <TableWrapper>
          <Table aria-label="Run history">
            <thead>
              <tr>
                <Th>Started</Th>
                <Th>Status</Th>
                <Th>Workload</Th>
                <Th align="right">Duration</Th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><Td colSpan={4} muted>No nightly runs found.</Td></tr>
              )}
              {runs.map(run => (
                <RunRow
                  key={run.runId}
                  selected={run.runId === selectedRun?.runId}
                  onClick={() => selectRun(run.runId)}
                >
                  <Td>{fmtDate(run.startedAt)}</Td>
                  <Td><StatusBadge tone={statusTone(run.status)}>{run.status}</StatusBadge></Td>
                  <Td title={displayEntityTaxonomyText(run.runLabel) || undefined}>{workloadLabel(run)}</Td>
                  <Td align="right">{duration(run.durationSeconds)}</Td>
                </RunRow>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        {list && (
          <ButtonRow style={{ marginTop: spacing[3], justifyContent: 'space-between' }}>
            <span>{list.totalCount.toLocaleString()} run{list.totalCount === 1 ? '' : 's'} — page {list.page} of {Math.max(1, list.totalPages)}</span>
            <span>
              <SecondaryButton disabled={loading || page <= 1} onClick={() => loadList(page - 1)}>← Previous</SecondaryButton>
              <SecondaryButton disabled={loading || page >= list.totalPages} onClick={() => loadList(page + 1)}>Next →</SecondaryButton>
            </span>
          </ButtonRow>
        )}
      </Card>

      <Card>
        <CardTitle>Run detail</CardTitle>
        {!selectedRun && <Banner tone="info">Select a run to view samples.</Banner>}
        {selectedRun && (
          <>
            <p>
              <strong>{displayEntityTaxonomyText(selectedRun.runLabel) || workloadLabel(selectedRun)}</strong><br />
              {selectedRun.workload === 'extract'
                ? `Entity type: ${selectedRun.entityTypes.map(displayEntityType).join(', ') || displayEntityTaxonomyText(selectedRun.entityLabel) || '—'}`
                : `WebIQ cohort: ${selectedRun.entityTypes.map(displayEntityType).join(', ') || displayEntityTaxonomyText(selectedRun.entityLabel) || '—'}`}
            </p>
            {selectedRun.webIqStats && (
              <>
                <h4>What the WebIQ refresh produced</h4>
                <TableWrapper style={{ marginBottom: spacing[4] }}>
                  <Table aria-label="WebIQ results">
                    <tbody>
                      {WEBIQ_STAT_LABELS
                        .filter(([key]) => selectedRun.webIqStats?.[key] !== undefined)
                        .map(([key, label]) => (
                          <tr key={key}>
                            <Td>{label}</Td>
                            <Td align="right">{webIqStatValue(selectedRun.webIqStats!, key)}</Td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </>
            )}
            {selectedRun.failures && (
              <>
                <h4>Operational failures</h4>
                <Pre>{JSON.stringify(selectedRun.failures, null, 2)}</Pre>
              </>
            )}
            <h4>Platform snapshots</h4>
            <SampleSummary>
              <div>
                <SampleSummaryLabel>Samples</SampleSummaryLabel>
                <SampleSummaryValue>{samples.length}</SampleSummaryValue>
                <SampleSummaryHint>one every ~10 min, plus one per phase change</SampleSummaryHint>
              </div>
              <div>
                <SampleSummaryLabel>Run covered</SampleSummaryLabel>
                <SampleSummaryValue>
                  {coverage.pct == null ? '—' : `${coverage.pct}%`}
                </SampleSummaryValue>
                <SampleSummaryHint>
                  {coverage.pct == null
                    ? 'run duration unknown'
                    : coverage.pct >= 90
                      ? 'the whole run is accounted for'
                      : 'phases outside this window were not sampled'}
                </SampleSummaryHint>
              </div>
              <div>
                <SampleSummaryLabel>Estimated cost</SampleSummaryLabel>
                <SampleSummaryValue>
                  {runCost == null ? '—' : `$${runCost.totalCost.toFixed(2)}`}
                </SampleSummaryValue>
                <SampleSummaryHint>
                  {runCost == null
                    ? 'no samples to integrate'
                    : runCost.partial
                      ? 'a floor — unsampled time is not counted'
                      : 'integrated from the samples below'}
                </SampleSummaryHint>
              </div>
              <div>
                <SampleSummaryLabel>Added by the run</SampleSummaryLabel>
                <SampleSummaryValue>
                  {runCost == null ? '—' : `$${runCost.incrementalCost.toFixed(2)}`}
                </SampleSummaryValue>
                <SampleSummaryHint>above the idle platform cost</SampleSummaryHint>
              </div>
            </SampleSummary>
            {samples.length > 0 && samples.every(s => !s.phase) && (
              <Banner tone="info">
                This run predates phase tagging. Only the queue drain emitted samples then, so the
                extraction preflight, scale-up and post-processing phases are missing rather than
                idle.
              </Banner>
            )}
            <TableWrapper>
              <Table aria-label="Run samples">                  <thead>
                    <tr>
                      <Th>Time</Th>
                      <Th>Elapsed</Th>
                      <Th>Phase</Th>
                      <Th>Queues</Th>
                      <Th align="right">Poison</Th>
                      <Th>Function App</Th>
                      <Th>Reader API</Th>
                      <Th>SQL</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {samples.length === 0 && (
                      <tr><Td colSpan={8} muted>No samples recorded for this run.</Td></tr>
                    )}
                    {samples.map(sample => {
                      const queueTotal = totalQueueDepth(sample);
                      return (
                        <tr key={sample.id}>
                          <Td>{fmtDate(sample.sampledAt)}</Td>
                          <Td>{duration(sample.elapsedSeconds)}</Td>
                          <Td>
                            {sample.phase
                              ? <PhaseBadge>{PHASE_LABELS[sample.phase] ?? sample.phase}</PhaseBadge>
                              : <span style={{ color: colors.text.tertiary }}>—</span>}
                          </Td>
                          <Td>
                            <Stack>
                              <QueueTotal>
                                <BarTrack><BarFill width={(queueTotal / queueMax) * 100} /></BarTrack>
                                <strong>{queueTotal.toLocaleString()}</strong>
                              </QueueTotal>
                              {queueDepthEntries(sample).map(([name, depth]) => (
                                <QueueLine key={name}>
                                  <span>{name}</span>
                                  <QueueCount>{depth == null ? '—' : depth.toLocaleString()}</QueueCount>
                                </QueueLine>
                              ))}
                            </Stack>
                          </Td>
                          <Td align="right">
                            {sample.poisonCount ? <span style={{ color: colors.error[600] }}>{sample.poisonCount.toLocaleString()}</span> : dash(sample.poisonCount)}
                          </Td>
                          <Td>
                            <Stack>
                              <MiniBar width={((sample.functionInstanceCount || 0) / functionMax) * 100} />
                              {metricLine([
                                sample.functionInstanceCount == null ? null : `${sample.functionInstanceCount} inst`,
                                percent(sample.functionCpuPercent),
                                sample.functionMemoryMb == null ? null : `${sample.functionMemoryMb} MB`,
                                sample.functionSku || null,
                              ])}
                            </Stack>
                          </Td>
                          <Td>
                            <Stack>
                              <MiniBar tone="success" width={((sample.readerReplicas || 0) / readerMax) * 100} />
                              {metricLine([
                                sample.readerReplicas == null ? null : `${sample.readerReplicas} repl`,
                                percent(sample.readerCpuPercent),
                                sample.readerMemoryMb == null ? null : `${sample.readerMemoryMb} MB`,
                              ])}
                            </Stack>
                          </Td>
                          <Td>
                            {metricLine([
                              sample.sqlVCores == null ? null : `${sample.sqlVCores} vCores`,
                              percent(sample.sqlCpuPercent),
                              sample.sqlDtuPercent == null ? null : `${percent(sample.sqlDtuPercent)} DTU`,
                            ])}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
            </TableWrapper>
          </>
        )}
      </Card>
    </AdminLayout>
  );
}

function PreviousRun({ run }: { run?: NightlyRun }) {
  if (!run) return <span style={{ color: colors.text.tertiary }}>—</span>;
  return (
    <Stack style={{ minWidth: 0 }}>
      <StatusBadge tone={statusTone(run.status)}>{run.status}</StatusBadge>
      <span style={{ fontSize: typography.fontSizes.xs, color: colors.text.secondary }}>
        {fmtDate(run.startedAt)} · {duration(run.durationSeconds)}
      </span>
    </Stack>
  );
}
